/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { listPendingVerifications, reviewRegistry } from '@/lib/onboarding';

/** Case-insensitive match over the searchable fields of a pending record. */
export const matchesPendingFilter = (u, filter) => {
  if (!filter) return true;
  const q = filter.toLowerCase();
  return ['full_name', 'email', 'registry_number', 'specialty', 'primary_work_location']
    .some((f) => (u[f] || '').toLowerCase().includes(q));
};

/**
 * useReviewQueue — data + dialog lifecycle for the admin registry review queue.
 * Extracted from AdminOnboardingPanel.jsx (Code Quality refactor) so the
 * component is a thin renderer.
 */
export const useReviewQueue = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null); // currently viewed record
  const [action, setAction] = useState(null);     // 'approve' | 'reject' | null
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [imgZoom, setImgZoom] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listPendingVerifications();
      setItems(data.pending || []);
    } catch (e) {
      console.error('Admin: pending verifications load failed', e);
      toast.error('Failed to load pending verifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => items.filter((u) => matchesPendingFilter(u, filter)), [items, filter]);

  const closeAllDialogs = () => {
    if (busy) return;
    setSelected(null);
    setAction(null);
    setReason('');
    setImgZoom(false);
  };

  const openEvidence = (u) => { setSelected(u); setImgZoom(true); };
  const openAction = (u, nextAction) => { setSelected(u); setAction(nextAction); setReason(''); };
  const openApprove = (u) => openAction(u, 'approve');
  const openReject = (u) => openAction(u, 'reject');
  /** From the evidence zoom dialog: close zoom and jump to a review action. */
  const quickAction = (nextAction) => { setImgZoom(false); setAction(nextAction); };

  const submitReview = async () => {
    if (!selected || !action) return;
    if (action === 'reject' && !reason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setBusy(true);
    try {
      await reviewRegistry(selected.id, action, action === 'reject' ? reason.trim() : null);
      const who = selected.full_name || 'User';
      toast.success(action === 'approve' ? `${who} approved` : `${who} rejected`);
      closeAllDialogs();
      await load();
    } catch (e) {
      console.error('Admin: registry review failed', e);
      toast.error(e?.response?.data?.detail?.message || 'Review action failed');
    } finally {
      setBusy(false);
    }
  };

  return {
    items, filtered, loading, filter, setFilter,
    selected, action, reason, setReason, busy, imgZoom,
    openEvidence, openApprove, openReject, quickAction, closeAllDialogs, submitReview,
  };
};
